/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { Divider, Tooltip, useTheme } from '@mui/material';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MenuOpen from '@mui/icons-material/MenuOpen';

import { MaterialIcon } from '../../../components/icons';
import { Translate, useTranslateMessage } from '../../../components/localization';
import defaultMenuLogo from '../../../assets/cai_logo_2c_white.svg';
import { appRefToIconMap } from '../../header/app-switcher/app-icons/appIconMap';
import { useHotkey } from '../../../HotKeyContext';
import { useUserContext } from '../../../components/user';
import { cacheItem, clearCachedItem, getCachedItem } from '../../../util/cache';

import {
  SidebarContainer,
  MobileSubMenuContainer,
  NavCluster,
  PushSubmenuPanel,
  MobileOptionRow,
  MobileOptionContent,
} from './styles';
import SubMenuContent from './SubMenuContent';
import { CDS_SIDEBAR_PIN_STORAGE_PREFIX, CDS_SIDEBAR_RESET_CHROME_EVENT } from './sidebarNavConstants';

const SidebarHeader = ({
  isMobile,
  initialMenuConfig,
  toggleSidebar,
  clearActivatedSubMenu,
  appRef,
  onAppIconClick,
  appIconAccessibilityLabel,
}) => {
  const handleAppIconKeyDown = e => {
    if (!onAppIconClick) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onAppIconClick();
    }
  };

  const handleMenuIconClick = () => {
    toggleSidebar();
    clearActivatedSubMenu();
  };

  return (
    <div className={cn('sidebar-logo-container', { 'is-mobile': isMobile })}>
      <div
        className={cn('app-icon', { 'app-icon--opens-drawer': onAppIconClick })}
        onClick={onAppIconClick}
        onKeyDown={handleAppIconKeyDown}
        role={onAppIconClick ? 'button' : undefined}
        tabIndex={onAppIconClick ? 0 : undefined}
        aria-label={onAppIconClick ? appIconAccessibilityLabel : undefined}
      >
        <img src={appRefToIconMap[appRef] || initialMenuConfig?.menuLogo || defaultMenuLogo} alt="" />
      </div>
      {isMobile && <MaterialIcon icon={MenuOpen} onClick={handleMenuIconClick} className="menu-icon" />}
    </div>
  );
};

const SidebarContent = ({
  isMobile,
  handleMenuItemClick,
  toggleSidebar,
  initialMenuConfig,
  preparedConfig,
  amIActive,
  appRef,
  onAppIconClick,
  isDesktopNavLayout = false,
}) => {
  const isPushDesktopNav = Boolean(isDesktopNavLayout && !isMobile);
  const navClusterRef = useRef(null);
  const translateMessage = useTranslateMessage();
  const theme = useTheme();

  const tooltipSlotProps = useMemo(() => {
    const isDark = theme.palette?.mode === 'dark';

    return {
      popper: {
        sx: {
          zIndex: 1300,
          '& .MuiTooltip-tooltip': {
            padding: '11px 16px',
            borderRadius: '10px',
            border: `1px solid ${isDark ? '#49454F' : '#CAC4D0'}`,
            background: isDark ? 'rgba(74, 70, 81, 0.5)' : 'rgba(255, 255, 255, 0.5)',
            boxShadow: isDark ? '0px 4px 20px 0px rgba(0, 0, 0, 0.20)' : '0px 4px 20px 0px rgba(101, 119, 136, 0.2)',
            backdropFilter: 'blur(21px)',
            color: isDark ? '#FEF7FF' : '#49454F',
            fontSize: '16px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            margin: '0 0 0 12px !important',
            maxWidth: 'none',
          },
        },
      },
    };
  }, [theme]);

  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const [activatedSubMenu, setActivatedSubMenu] = useState(null);
  const [enableSearch, setEnableSearch] = useState(false);
  const [isPanelPinned, setIsPanelPinned] = useState(false);

  const { user } = useUserContext();

  const userIdentityKey = user?.email || user?.id || user?.username || 'anon';
  const pinStorageKey = useMemo(
    () => `${CDS_SIDEBAR_PIN_STORAGE_PREFIX}${appRef || 'app'}:${userIdentityKey}`,
    [appRef, userIdentityKey]
  );

  const activateSubMenu = useCallback(option => {
    if (option?.children?.length) {
      setActivatedSubMenu(option);
      setIsSubMenuOpen(true);
    }
  }, []);

  const clearActivatedSubMenu = useCallback(() => {
    if (isPanelPinned) clearCachedItem(pinStorageKey);

    setActivatedSubMenu(null);
    setIsSubMenuOpen(false);
    setEnableSearch(false);
    setIsPanelPinned(false);
  }, [isPanelPinned, pinStorageKey]);

  const persistPinnedSection = useCallback(
    (sectionId, pinned) => {
      if (pinned) {
        cacheItem(pinStorageKey, JSON.stringify({ sectionId: sectionId || null, pinned: true }));
      } else {
        clearCachedItem(pinStorageKey);
      }
    },
    [pinStorageKey]
  );

  const handlePinToggle = useCallback(() => {
    const next = !isPanelPinned;

    setIsPanelPinned(next);
    persistPinnedSection(activatedSubMenu?.id, next);
  }, [activatedSubMenu?.id, isPanelPinned, persistPinnedSection]);

  const handleRequestClosePanel = useCallback(() => {
    clearActivatedSubMenu();
  }, [clearActivatedSubMenu]);

  useEffect(() => {
    if (!isPushDesktopNav) return;
    if (!preparedConfig.length) return;

    const raw = getCachedItem(pinStorageKey);

    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);

      if (!parsed?.pinned || !parsed?.sectionId) return;

      const found = preparedConfig.find(o => o.id === parsed.sectionId && o.children?.length);

      if (!found) {
        clearCachedItem(pinStorageKey);

        return;
      }

      setActivatedSubMenu(found);
      setIsSubMenuOpen(true);
      setIsPanelPinned(true);
      setEnableSearch(Boolean(found.enableSearch));
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[sidebar] Failed to parse pinned section state', err);
      }

      clearCachedItem(pinStorageKey);
    }
  }, [isPushDesktopNav, pinStorageKey, preparedConfig]);

  useEffect(() => {
    if (!isPushDesktopNav) return;

    const onReset = () => clearActivatedSubMenu();

    window.addEventListener(CDS_SIDEBAR_RESET_CHROME_EVENT, onReset);

    return () => window.removeEventListener(CDS_SIDEBAR_RESET_CHROME_EVENT, onReset);
  }, [isPushDesktopNav, clearActivatedSubMenu]);

  useEffect(() => {
    if (!isPushDesktopNav || !isSubMenuOpen || !activatedSubMenu || isPanelPinned) return;

    const onPointerDown = e => {
      const root = navClusterRef.current;

      if (!root || root.contains(e.target)) return;

      clearActivatedSubMenu();
    };

    document.addEventListener('pointerdown', onPointerDown, true);

    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [activatedSubMenu, clearActivatedSubMenu, isPanelPinned, isPushDesktopNav, isSubMenuOpen]);

  const openMasterDataSearch = useCallback(() => {
    const option = preparedConfig.find(o => o.id === 'master-data');

    if (!option?.children?.length) return;

    if (isPushDesktopNav) {
      setIsPanelPinned(false);
      persistPinnedSection(null, false);
    }

    activateSubMenu(option);
    setEnableSearch(Boolean(option.enableSearch));
    setIsSubMenuOpen(true);
  }, [activateSubMenu, isPushDesktopNav, persistPinnedSection, preparedConfig]);

  const handleEscapeNav = useCallback(() => {
    if (isPushDesktopNav && isPanelPinned) return;

    const parentId = activatedSubMenu?.id;

    clearActivatedSubMenu();

    if (parentId && navClusterRef.current) {
      const parentBtn = navClusterRef.current.querySelector(`button[data-testid="${parentId}"]`);

      if (parentBtn && typeof parentBtn.focus === 'function') parentBtn.focus();
    }
  }, [activatedSubMenu?.id, clearActivatedSubMenu, isPanelPinned, isPushDesktopNav]);

  const hotkeys = useMemo(
    () =>
      isMobile
        ? [{ key: 'Escape', callback: handleEscapeNav }]
        : [
            { key: '/', callback: openMasterDataSearch, options: { appRefsScope: ['company-admin', 'dispatch-exp'] } },
            { key: 'Escape', callback: handleEscapeNav },
          ],
    [handleEscapeNav, isMobile, openMasterDataSearch]
  );

  useHotkey(hotkeys);

  const handlePushParentClick = useCallback(
    option => {
      const sameOpen = activatedSubMenu?.id === option.id && isSubMenuOpen;

      if (sameOpen && !isPanelPinned) {
        clearActivatedSubMenu();

        return;
      }

      activateSubMenu(option);
      setEnableSearch(Boolean(option.enableSearch));
      setIsSubMenuOpen(true);

      if (isPanelPinned) {
        persistPinnedSection(option.id, true);
      }
    },
    [activateSubMenu, activatedSubMenu, clearActivatedSubMenu, isPanelPinned, isSubMenuOpen, persistPinnedSection]
  );

  const handleOptionClick = useCallback(
    (option, hasChildren) => {
      if (hasChildren) {
        if (isMobile) {
          activateSubMenu(option);
          setEnableSearch(option?.enableSearch);
        } else if (isPushDesktopNav) {
          handlePushParentClick(option);
        }

        return;
      }

      handleMenuItemClick(option.path);

      if (isPushDesktopNav && !isPanelPinned) clearActivatedSubMenu();

      if (isMobile) toggleSidebar();
    },
    [
      activateSubMenu,
      clearActivatedSubMenu,
      handleMenuItemClick,
      handlePushParentClick,
      isMobile,
      isPanelPinned,
      isPushDesktopNav,
      toggleSidebar,
    ]
  );

  const subMenuProps = useMemo(
    () => ({
      activatedSubMenu,
      isMobile,
      clearActivatedSubMenu,
      enableSearch,
      amIActive,
      handleMenuItemClick,
      toggleSidebar,
      isSubMenuOpen,
      isPanelPinned,
      onPinToggle: handlePinToggle,
      onRequestClosePanel: handleRequestClosePanel,
      panelVariant: isPushDesktopNav ? 'push-desktop' : 'mobile',
    }),
    [
      activatedSubMenu,
      amIActive,
      clearActivatedSubMenu,
      enableSearch,
      handleMenuItemClick,
      handlePinToggle,
      handleRequestClosePanel,
      isMobile,
      isPanelPinned,
      isPushDesktopNav,
      isSubMenuOpen,
      toggleSidebar,
    ]
  );

  const panelIsOpen = Boolean(isPushDesktopNav && activatedSubMenu && isSubMenuOpen);
  const menuTitleDomId = activatedSubMenu?.id ? `cds-sidebar-section-title-${activatedSubMenu.id}` : undefined;

  const renderMobileOption = (option, key, hasChildren, optionClass) => (
    <MobileOptionRow
      key={key}
      data-testid={option?.id}
      className={optionClass}
      onClick={() => handleOptionClick(option, hasChildren)}
    >
      <MobileOptionContent>
        <div className="mobile-option-label">
          {option?.icon || option?.iconName ? (
            <MaterialIcon icon={option.icon} iconName={option.iconName} className="icon" />
          ) : null}
          <Translate className="label" stringId={option?.labelStringId} />
        </div>
        {hasChildren && <MaterialIcon icon={ArrowForwardRounded} />}
      </MobileOptionContent>
    </MobileOptionRow>
  );

  const renderParentRail = (option, key, panelOpenForOption, tooltipId, optionClass) => (
    <Tooltip
      key={key}
      title={tooltipId ? translateMessage(tooltipId) : ''}
      placement="right"
      enterDelay={500}
      enterNextDelay={500}
      disableHoverListener={panelOpenForOption}
      slotProps={tooltipSlotProps}
    >
      <button
        type="button"
        data-testid={option?.id}
        className={optionClass}
        aria-expanded={panelOpenForOption}
        aria-controls={isPushDesktopNav ? 'cds-sidebar-submenu-panel' : undefined}
        onClick={() => handleOptionClick(option, true)}
      >
        {option?.icon || option?.iconName ? (
          <MaterialIcon icon={option.icon} iconName={option.iconName} className="icon" />
        ) : null}
        <MaterialIcon
          icon={ExpandMore}
          className={cn('rail-parent-chevron', { 'rail-parent-chevron--open': panelOpenForOption })}
          aria-hidden
        />
      </button>
    </Tooltip>
  );

  const renderLeafRail = (option, key, tooltipId, optionClass) => (
    <Tooltip
      key={key}
      title={tooltipId ? translateMessage(tooltipId) : ''}
      placement="right"
      enterDelay={500}
      enterNextDelay={500}
      slotProps={tooltipSlotProps}
    >
      <button
        type="button"
        data-testid={option?.id}
        className={optionClass}
        onClick={() => handleOptionClick(option, false)}
      >
        {option?.icon || option?.iconName ? (
          <MaterialIcon icon={option.icon} iconName={option.iconName} className="icon" />
        ) : null}
      </button>
    </Tooltip>
  );

  const railBody = (
    <SidebarContainer
      className={cn('sidebar', {
        'rail-no-hover': !isMobile,
        'sidebar-secondary': initialMenuConfig?.sidebarVariant === 'secondary',
        'is-mobile': isMobile,
      })}
    >
      <SidebarHeader
        appRef={appRef}
        isMobile={isMobile}
        initialMenuConfig={initialMenuConfig}
        toggleSidebar={toggleSidebar}
        clearActivatedSubMenu={clearActivatedSubMenu}
        onAppIconClick={onAppIconClick}
        appIconAccessibilityLabel={undefined}
      />
      <div
        className={cn('sidebar-options-container', {
          hidden: isSubMenuOpen && isMobile,
        })}
      >
        {preparedConfig?.map((option, index) => {
          const hasChildren = Boolean(option?.children?.length);
          const isDivider = option?.id === 'divider';
          const key = option?.id ? `${option.id}-${index}` : `item-${index}`;

          if (isDivider) {
            return <Divider sx={{ width: '80%' }} key={key} variant="middle" />;
          }

          const panelOpenForOption = hasChildren && activatedSubMenu?.id === option.id && isSubMenuOpen;
          const tooltipId = option?.tooltipStringId || option?.labelStringId;

          const railActive =
            amIActive(option) || (hasChildren && Boolean(option.children?.some(child => amIActive(child))));

          const optionClass = cn('option', { option__active: railActive });

          if (isMobile) return renderMobileOption(option, key, hasChildren, optionClass);
          if (hasChildren) return renderParentRail(option, key, panelOpenForOption, tooltipId, optionClass);

          return renderLeafRail(option, key, tooltipId, optionClass);
        })}
      </div>
      {isMobile && activatedSubMenu ? (
        <MobileSubMenuContainer>
          <div className={cn('menu-sidebar', { hidden: !isSubMenuOpen && isMobile })}>
            <SubMenuContent menuTitleDomId={menuTitleDomId} {...subMenuProps} />
          </div>
        </MobileSubMenuContainer>
      ) : null}
    </SidebarContainer>
  );

  const pushPanel = isPushDesktopNav ? (
    <PushSubmenuPanel
      id="cds-sidebar-submenu-panel"
      $isOpen={panelIsOpen}
      className={cn('menu-sidebar', 'submenu--push-desktop', {
        'activated-sub-menu': panelIsOpen,
      })}
      aria-hidden={!panelIsOpen}
    >
      {panelIsOpen ? (
        <div className="push-panel-inner">
          <SubMenuContent menuTitleDomId={menuTitleDomId} {...subMenuProps} />
        </div>
      ) : null}
    </PushSubmenuPanel>
  ) : null;

  if (isPushDesktopNav) {
    return (
      <NavCluster ref={navClusterRef}>
        {railBody}
        {pushPanel}
      </NavCluster>
    );
  }

  return railBody;
};

export default SidebarContent;
